import { makeObservable, runInAction } from 'mobx';

import type { IRachisMessage, ListResponse, TConstraints } from '@feathr/rachis';
import { isWretchError, wretch } from '@feathr/rachis';

import type { EmailVerification, EmailVerifications } from '../email_verifications';
import { ECollectionClassName } from '../model';
import { Campaign } from './campaign';
import type { IAudienceSegmentIds, ICampaignSegment, IEmailBaseCampaign } from './types';

export abstract class EmailBaseCampaign<
  T extends IEmailBaseCampaign = IEmailBaseCampaign,
> extends Campaign<T> {
  public override get constraints(): TConstraints<IEmailBaseCampaign> {
    return {
      ...super.constraints,
      'consent.has_consent': {
        presence: {
          allowEmpty: false,
          message: '^You must provide consent to publish this campaign',
        },
        exclusion: {
          within: [false],
          message: '^You must provide consent to publish this campaign',
        },
      },
      'consent.user': {
        presence: {
          allowEmpty: false,
          message: '^Only a valid user may provide consent',
        },
      },
      'consent.date_consented': {
        datetime: true,
        presence: {
          allowEmpty: false,
          message: '^You must provide a valid group or remove the goal',
        },
      },
    };
  }

  constructor(attributes: Partial<T>) {
    super(attributes);

    makeObservable(this);
  }

  public get audienceSegmentIds(): IAudienceSegmentIds {
    const segments: ICampaignSegment[] = this.get('segments') ?? [];
    return {
      excludedSegmentIds: segments
        .filter((segment) => !segment.included && segment.id)
        .map((segment) => segment.id),
      includedSegmentIds: segments
        .filter((segment) => segment.included && segment.id)
        .map((segment) => segment.id),
    };
  }

  // Whether the campaign emails every address stored for a person rather than only the primary.
  // Only the Mailrail and Pinpoint shapes expose the setting; Drip campaigns inherit this false.
  public get includesAdditionalEmails(): boolean {
    return false;
  }

  public async sendTestEmail(request: {
    recipient_emails: string[];
    recipient_id: string;
  }): Promise<void> {
    this.assertCollection(this.collection);
    this.assertId(this.id, ECollectionClassName.EmailBaseCampaign);

    const url = `${BLACKBOX_URL}flight_campaigns/${this.id}/test`;
    const headers = this.collection.getHeaders();
    const response = await wretch<IRachisMessage>(url, {
      headers,
      body: JSON.stringify(request),
      method: 'POST',
    });
    if (isWretchError(response)) {
      runInAction(() => {
        this.isUpdating = false;
        this.isErrored = true;
        this.error = response.error;
      });
      throw response.error;
    } else {
      runInAction(() => {
        this.isUpdating = false;
        this.isErrored = false;
        this.error = null;
      });
    }
  }

  public getEmailVerifications(
    EmailVerificationsCollection: EmailVerifications,
  ): ListResponse<EmailVerification> {
    return EmailVerificationsCollection.list({ filters: { email: this.get('from_address') } });
  }

  public getEmailVerification(
    emailVerifications: ListResponse<EmailVerification>,
  ): EmailVerification | undefined {
    return emailVerifications &&
      !emailVerifications.isErrored &&
      emailVerifications.models.length > 0
      ? emailVerifications.models[0]
      : undefined;
  }
}

// Function that allows type narrowing for Email Campaigns.
export function isEmailMarketingCampaign(campaign: Campaign): campaign is EmailBaseCampaign {
  // Difference: includes Drip campaigns
  return campaign.isAnyEmail;
}
